import { ParentRatingRows, RatingsKind, RatingsTableSaveOutcome } from '@cadstrom/cadstrom-shared';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	differentialPairsFromPackage,
	differentialPairsToPackage,
} from '#components/differential-pairs/differential-pair.js';
import {
	PACKAGE_EDITOR_RATINGS_OVERRIDES_FLAG,
	PACKAGE_EDITOR_RATINGS_ROWS_FLAG,
} from '#components/feature-flag/flags.js';
import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { RATING_ROW_NOUN } from '#components/ratings-table/rating-row.js';
import { EvidenceRowPicks } from '#package-store/package-editor/evidence-row-picks.js';
import { useEvidenceRowPickSelection } from '#package-store/package-family-editor/package-evidence-panel/use-evidence-row-pick-selection.js';
import { usePackageSidePanelStore } from '#package-store/package-family-editor/side-panel/package-side-panel-store.js';
import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { BoundariesEditor } from '#screens/boundaries-editor/boundaries-editor.js';
import { BoundariesViewModeSchema } from '#screens/boundaries-editor/boundaries-view-mode.js';
import { RatingsRowsView } from '#screens/normalized-ratings/ratings-rows-view.js';
import { uncoveredBoundaryIds } from '#screens/normalized-ratings/uncovered-boundaries.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useSchemaLocalStorage } from '#utils/use-schema-cookie.js';
import { getAbsoluteMaximumRatingsBoundariesContextValue } from './amr-boundaries-context.js';
import { BoundariesSavePreview } from './boundaries-save-preview.js';
import { getOperatingConditionBoundariesContextValue } from './operating-condition-boundaries-context.js';

import type { HilDifferentialPair } from '@cadstrom/backend/shared';
import type { ParentRatingRow, StoredRatingsTable } from '@cadstrom/cadstrom-shared';
import type { ComponentBoundary, ModeBase } from '@cadstrom/sigma-engine-shared';
import type { SetStateAction } from 'react';
import type { SelectedBoundaryTableRow } from '#screens/boundaries-editor/boundaries-editor.js';
import type { BoundariesViewMode } from '#screens/boundaries-editor/boundaries-view-mode.js';
import type {
	SimplifiedBoundaryTableMode,
	SimplifiedBoundaryTableRowModel,
} from '#screens/boundaries-editor/simplified-boundary-table/simplified-boundary-table-utils.js';
import type { RatingsRowsViewChange } from '#screens/normalized-ratings/ratings-rows-view.js';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/boundaries/',
)({
	staticData: {
		navigation: {
			label: 'Package: Boundaries',
			keywords: ['limits', 'operating', 'conditions', 'amr'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	// The rows view opens on the draft once, on mount, and saves every edit against what it opened on. A
	// mount that outlived a package switch would save the previous package's rows onto the one now on
	// screen, so the tab remounts on the package id instead.
	remountDeps: ({ params }) => ({ packageId: params.packageId }),
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Boundaries • Package Editor • Cadstrom',
			},
		],
	}),
	component: BoundariesTab,
});

/** The route hash the uncovered-boundaries banner sends, naming the view to open. */
const BANNER_HASH_VIEW_MODE: Record<string, BoundariesViewMode | undefined> = {
	'all-boundaries': 'all',
};

/** The rows of each kind's rows view that still need a reference, by id; empty when the kind can be saved. */
interface RowsNeedingReference {
	amr: string[];
	oc: string[];
}

/**
 * The draft the tab saves. The flat tables edit the boundaries; the rows view rewrites the boundaries,
 * the modes and the pairs together with its table and parent rows. Each metadata entry is as the
 * package holds it until an edit changes it. A type alias rather than an interface: the autosave hook
 * takes a record, which an interface's missing index signature does not satisfy.
 */
type BoundariesDraft = {
	boundaries: ComponentBoundary[];
	modes: ModeBase[];
	differentialPairs: HilDifferentialPair[];
	amrRatingsTable?: StoredRatingsTable | undefined;
	ocRatingsTable?: StoredRatingsTable | undefined;
	parentAmrRows?: ParentRatingRow[] | undefined;
	parentOcRows?: ParentRatingRow[] | undefined;
};

/**
 * The part of a draft a save writes, printed for the save preview.
 *
 * @param draft - The draft, or the package as saved in the same shape.
 * @returns Pretty-printed JSON.
 */
function printSavedFields(draft: BoundariesDraft): string {
	return JSON.stringify(
		{
			boundaries: draft.boundaries,
			modes: draft.modes,
			differentialPairs: draft.differentialPairs,
			metadata: {
				amrRatingsTable: draft.amrRatingsTable,
				ocRatingsTable: draft.ocRatingsTable,
				parentAmrRows: draft.parentAmrRows,
				parentOcRows: draft.parentOcRows,
			},
		},
		undefined,
		'\t',
	);
}

function BoundariesTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();
	const showsRowsView = useFeatureFlag(PACKAGE_EDITOR_RATINGS_ROWS_FLAG) === true;
	const overridesEditable = useFeatureFlag(PACKAGE_EDITOR_RATINGS_OVERRIDES_FLAG) === true;
	const [storedViewMode, setStoredViewMode] = useSchemaLocalStorage(
		BoundariesViewModeSchema,
		'boundaries-tab-view-mode',
		'all',
	);
	const [ownShowsRows, setOwnShowsRows] = useState(true);

	// The uncovered-boundaries banner links here with a hash naming the view: while it is in the URL it
	// opens that view. The viewer's first choice of table or view drops the hash, so the choice stands
	// and the next click on the banner is seen again.
	const { hash } = useLocation();
	const navigate = useNavigate();
	const hashViewMode = BANNER_HASH_VIEW_MODE[hash.replace(/^#/, '')];
	const dropHash = () => {
		if (hashViewMode !== undefined) {
			void navigate({ hash: '', replace: true, resetScroll: false });
		}
	};
	const viewMode = hashViewMode ?? storedViewMode;
	const [rowsNeedingReference, setRowsNeedingReference] = useState<RowsNeedingReference>({ amr: [], oc: [] });
	const setViewMode = (next: BoundariesViewMode) => {
		dropHash();
		setStoredViewMode(next);
	};
	const showsRows = hashViewMode !== undefined || ownShowsRows;
	const setShowsRows = (next: boolean) => {
		dropHash();
		setOwnShowsRows(next);
	};
	// Only the rows view on screen blocks the save: its pre-filled rows exist nowhere but there, so once it
	// is put away the draft holds nothing that needs a reference.
	const activeKind =
		!showsRows ? null
		: viewMode === 'amr' ? RatingsKind.Amr
		: viewMode === 'operating' ? RatingsKind.Oc
		: null;
	const blockedRows =
		activeKind === RatingsKind.Amr ? rowsNeedingReference.amr
		: activeKind === RatingsKind.Oc ? rowsNeedingReference.oc
		: [];
	const blockedReason =
		blockedRows.length === 0 || activeKind === null ?
			undefined
		:	`${blockedRows.length} ${RATING_ROW_NOUN[activeKind === RatingsKind.Amr ? 'amrs' : 'ocs']}${blockedRows.length === 1 ? ' needs' : 's need'} a reference before the package can be saved.`;

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);
	const initialDraft: BoundariesDraft = {
		boundaries: pkg.boundaries,
		modes: pkg.modes,
		differentialPairs: pkg.differentialPairs,
		amrRatingsTable: pkg.metadata.amrRatingsTable,
		ocRatingsTable: pkg.metadata.ocRatingsTable,
		parentAmrRows: pkg.metadata.parentAmrRows,
		parentOcRows: pkg.metadata.parentOcRows,
	};
	// The package as last saved, kept here as well as in the autosave hook so the validity rule below can
	// read it: a blocked rows view only matters while the draft holds something unsaved.
	const [savedDraft, setSavedDraft] = useState<BoundariesDraft>(initialDraft);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				const saved: BoundariesDraft = {
					boundaries: res.pkg.boundaries,
					modes: res.pkg.modes,
					differentialPairs: res.pkg.differentialPairs,
					amrRatingsTable: res.pkg.metadata.amrRatingsTable,
					ocRatingsTable: res.pkg.metadata.ocRatingsTable,
					parentAmrRows: res.pkg.metadata.parentAmrRows,
					parentOcRows: res.pkg.metadata.parentOcRows,
				};
				setServerDraft(saved);
				setSavedDraft(saved);
			},
		}),
	);

	const {
		draftState: draft,
		setDraftState: setDraft,
		serverState: serverDraft,
		setServerState: setServerDraft,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: initialDraft,
		mutationKeys: trpc.package.update.mutationKey(),
		// A clean draft is always valid, blocked rows view or not: there is nothing to hold back, and an
		// invalid draft would stop the viewer leaving the route.
		isValid: (draft: BoundariesDraft) => blockedReason === undefined || isEqual(draft, savedDraft),
		saveFn: async (draft) => {
			// Only what an edit changed is sent: the flat tables touch the boundaries and the parent rows
			// they prune, while the rows view also rewrites modes, pairs and its own table.
			const metadata = {
				...(draft.parentAmrRows === pkg.metadata.parentAmrRows ? {} : { parentAmrRows: draft.parentAmrRows }),
				...(draft.parentOcRows === pkg.metadata.parentOcRows ? {} : { parentOcRows: draft.parentOcRows }),
				...(draft.amrRatingsTable === pkg.metadata.amrRatingsTable ? {} : { amrRatingsTable: draft.amrRatingsTable }),
				...(draft.ocRatingsTable === pkg.metadata.ocRatingsTable ? {} : { ocRatingsTable: draft.ocRatingsTable }),
			};
			await updatePackage({
				id: pkg.id,
				data: {
					boundaries: draft.boundaries,
					...(draft.modes === pkg.modes ? {} : { modes: draft.modes }),
					...(draft.differentialPairs === pkg.differentialPairs ?
						{}
					:	{ differentialPairs: { set: draft.differentialPairs } }),
					...(Object.keys(metadata).length === 0 ? {} : { metadata }),
				},
			});
		},
	});

	// Deleting a boundary in a flat table must also drop its id from the datasheet-row metadata, so the
	// draft prunes the parent rows alongside. Rows that lost nothing keep their identity.
	const setDraftBoundaries = useCallback(
		(update: SetStateAction<ComponentBoundary[]>) =>
			setDraft((previous) => {
				const boundaries = typeof update === 'function' ? update(previous.boundaries) : update;
				return {
					...previous,
					boundaries,
					parentAmrRows: ParentRatingRows.pruneBoundaryIds(previous.parentAmrRows, boundaries),
					parentOcRows: ParentRatingRows.pruneBoundaryIds(previous.parentOcRows, boundaries),
				};
			}),
		[setDraft],
	);

	const applyRowsViewChange = useCallback(
		(kind: RatingsKind, change: RatingsRowsViewChange) => {
			setRowsNeedingReference((previous) => ({
				...previous,
				[kind === RatingsKind.Amr ? 'amr' : 'oc']:
					change.outcome === RatingsTableSaveOutcome.Refused ? change.rowsNeedingReference : [],
			}));
			if (change.outcome === RatingsTableSaveOutcome.Refused) {
				return;
			}
			setDraft((previous) => ({
				...previous,
				boundaries: change.boundaries,
				// Modes and pairs are kept as the package holds them unless the editor changed them, so a rows
				// edit does not send either list back and opening the pairs editor does not rewrite every pair.
				modes: isEqual(change.modes, previous.modes) ? previous.modes : change.modes,
				differentialPairs:
					isEqual(change.differentialPairs, differentialPairsFromPackage(previous.differentialPairs)) ?
						previous.differentialPairs
					:	differentialPairsToPackage(change.differentialPairs, pkg.signals),
				...(kind === RatingsKind.Amr ?
					{ amrRatingsTable: change.table, parentAmrRows: change.parentRows }
				:	{ ocRatingsTable: change.table, parentOcRows: change.parentRows }),
			}));
		},
		[setDraft, pkg.signals],
	);

	const draftBoundaries = draft.boundaries;
	const amrsContextValue = useMemo(() => {
		return getAbsoluteMaximumRatingsBoundariesContextValue({
			signals: pkg.signals,
			boundaries: draftBoundaries,
			updateBoundaries: setDraftBoundaries,
		});
	}, [pkg, draftBoundaries, setDraftBoundaries]);
	const ocsContextValue = useMemo(() => {
		return getOperatingConditionBoundariesContextValue({
			signals: pkg.signals,
			modes: draft.modes,
			boundaries: draftBoundaries,
			updateBoundaries: setDraftBoundaries,
		});
	}, [pkg, draft.modes, draftBoundaries, setDraftBoundaries]);

	const { selectedRow, select: selectRow } = useEvidenceRowPickSelection<SelectedBoundaryTableRow>();
	const handleSelectedRowChange = useCallback(
		(row: SimplifiedBoundaryTableRowModel | null, tableMode: SimplifiedBoundaryTableMode) =>
			row === null ?
				selectRow(null, null)
			:	selectRow(
					{ tableMode, rowId: row.id },
					EvidenceRowPicks.forBoundaryRow(row, tableMode, pkg.id, pkg.signals, pkg.modes),
				),
		[pkg, selectRow],
	);
	const handleSelectedBoundaryChange = useCallback(
		(boundary: ComponentBoundary | null) =>
			boundary === null ?
				selectRow(null, null)
			:	selectRow(
					{ tableMode: null, rowId: boundary.id },
					EvidenceRowPicks.forBoundary(boundary, pkg.id, pkg.signals, pkg.modes, {
						amrs: pkg.metadata.parentAmrRows,
						ocs: pkg.metadata.parentOcRows,
					}),
				),
		[pkg, selectRow],
	);

	// The bounds no rating row record links to, per kind: live from a mounted rows view, which knows
	// the rows it has deleted, else read from the draft. Flagged in the All boundaries table and counted
	// in the banner above the tabs, which the side-panel store carries up to it.
	const [liveUncovered, setLiveUncovered] = useState<Record<RatingsKind, string[] | null>>({
		[RatingsKind.Amr]: null,
		[RatingsKind.Oc]: null,
	});
	const reportUncovered = useCallback(
		(kind: RatingsKind, boundaryIds: string[] | null) =>
			setLiveUncovered((previous) => ({ ...previous, [kind]: boundaryIds })),
		[],
	);
	const uncoveredByKind = useMemo(() => {
		const ofKind = (kind: RatingsKind): string[] =>
			liveUncovered[kind] ??
			uncoveredBoundaryIds(kind, {
				boundaries: draft.boundaries,
				table: kind === RatingsKind.Amr ? draft.amrRatingsTable : draft.ocRatingsTable,
				parentRows: kind === RatingsKind.Amr ? draft.parentAmrRows : draft.parentOcRows,
			});
		return { amr: ofKind(RatingsKind.Amr), oc: ofKind(RatingsKind.Oc) };
	}, [liveUncovered, draft]);
	const uncoveredIds = useMemo(() => new Set([...uncoveredByKind.amr, ...uncoveredByKind.oc]), [uncoveredByKind]);
	const setUncoveredBoundaryCounts = usePackageSidePanelStore((state) => state.setUncoveredBoundaryCounts);
	useEffect(() => {
		setUncoveredBoundaryCounts({ amrCount: uncoveredByKind.amr.length, ocCount: uncoveredByKind.oc.length });
	}, [setUncoveredBoundaryCounts, uncoveredByKind]);
	useEffect(() => () => setUncoveredBoundaryCounts(null), [setUncoveredBoundaryCounts]);

	const el = useDataElTagGenerator(BoundariesTab);
	return (
		<div {...el()} ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 flex-col px-3 py-2'>
			<BoundariesEditor
				boundaries={draftBoundaries}
				onChange={setDraftBoundaries}
				signals={pkg.signals}
				modes={draft.modes}
				parentAmrRows={draft.parentAmrRows}
				parentOcRows={draft.parentOcRows}
				rowsViews={
					showsRowsView ?
						{
							amrs: (
								<RatingsRowsView
									key='amrs'
									kind={RatingsKind.Amr}
									signals={pkg.signals}
									pkg={{
										boundaries: draft.boundaries,
										modes: draft.modes,
										differentialPairs: draft.differentialPairs,
										table: draft.amrRatingsTable,
										parentRows: draft.parentAmrRows,
									}}
									showPreview
									overridesEditable={overridesEditable}
									onUncoveredChange={(boundaryIds) => reportUncovered(RatingsKind.Amr, boundaryIds)}
									onChange={(change) => applyRowsViewChange(RatingsKind.Amr, change)}
								/>
							),
							ocs: (
								<RatingsRowsView
									key='ocs'
									kind={RatingsKind.Oc}
									signals={pkg.signals}
									pkg={{
										boundaries: draft.boundaries,
										modes: draft.modes,
										differentialPairs: draft.differentialPairs,
										table: draft.ocRatingsTable,
										parentRows: draft.parentOcRows,
									}}
									showPreview
									overridesEditable={overridesEditable}
									onUncoveredChange={(boundaryIds) => reportUncovered(RatingsKind.Oc, boundaryIds)}
									onChange={(change) => applyRowsViewChange(RatingsKind.Oc, change)}
								/>
							),
						}
					:	undefined
				}
				saveActions={
					showsRowsView ?
						<BoundariesSavePreview
							readChanges={() => ({ before: printSavedFields(serverDraft), after: printSavedFields(draft) })}
							blockedReason={blockedReason}
						/>
					:	undefined
				}
				viewMode={viewMode}
				onViewModeChange={setViewMode}
				showsRows={showsRows}
				onShowsRowsChange={setShowsRows}
				uncoveredBoundaryIds={uncoveredIds}
				selectedRow={selectedRow}
				onSelectedRowChange={handleSelectedRowChange}
				onSelectedBoundaryChange={handleSelectedBoundaryChange}
				showSaveButton={{
					saveDisabled: !isDirty || blockedReason !== undefined,
					disabledReason: blockedReason,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>

			<SetAiExtractionContext value={amrsContextValue} />
			<SetAiExtractionContext value={ocsContextValue} />
		</div>
	);
}
